// src/components/dashboards/add_patient_v2/components/AddMoreDetailsPanel.tsx
// -----------------------------------------------------------------------------
// AddMoreDetailsPanel — the collapsible "Add more details ▾" block inside a
// PatientCard.
//
// Joan, 2026-06-03 (cursor_logs/036 §16): external_id was pulled OUT of this
// panel and moved into the always-visible PatientCard header (with its own
// Autocomplete + tooltip). Having the field in two places confused the form;
// now there is exactly one canonical input for it. This panel is therefore
// description-only, but kept as a component because:
//   - the parent toggles it via `detailsOpen`, mirroring V1's affordance,
//   - the voice-to-text plumbing for the description field already lives here,
//   - future per-patient metadata (date_of_birth, gender, etc.) will land here
//     without changing PatientCard's render tree.
//
// `date_of_birth`, `gender`, `date_of_death` are intentionally OUT of scope.
// Users who want to record those before extraction type them into description.
// -----------------------------------------------------------------------------

import {
  Box,
  Collapse,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useRef, useState } from "react";
import { useTranslation } from "../../../../i18n";
import VoiceToTextButton from "components/chat/VoiceToTextButton";

export interface AddMoreDetailsPanelProps {
  open: boolean;
  description: string;
  onChangeDescription: (value: string) => void;
  disabled?: boolean;
  /** Anonymised name of the matched existing patient (when the card is in
   *  merge mode). When set, the description hint copy switches to mention
   *  the patient that this text will be attached to. See cursor_logs/036 §16. */
  matchedPatientName?: string | null;
}

const AddMoreDetailsPanel: React.FC<AddMoreDetailsPanelProps> = ({
  open,
  description,
  onChangeDescription,
  disabled,
  matchedPatientName,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Latest description, read inside the (async) voice-transcript callback so the
  // dictated text appends to whatever the user currently has, not a stale value.
  const descRef = useRef(description);
  descRef.current = description;

  // Lock the description (read-only) while the mic is recording/transcribing so
  // dictated text doesn't collide with manual typing (Joan, 2026-05-29). Read-only
  // (not disabled) keeps the mic's ✕/✓ controls clickable.
  const [voiceActive, setVoiceActive] = useState(false);

  return (
    <Collapse in={open} unmountOnExit>
      <Stack
        spacing={1.5}
        sx={{
          mt: 0.5,
          p: 1.5,
          borderRadius: theme.shape.borderRadius / 6,
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.dark,
            0.04,
          )} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.45)}`,
        }}
      >
        <Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={6}
            label={t("patients_v2.description_label", "Description")}
            value={description}
            onChange={(e) => onChangeDescription(e.target.value)}
            disabled={disabled}
            InputLabelProps={{ sx: { fontSize: "0.875rem", fontWeight: 500 } }}
            InputProps={{
              readOnly: voiceActive,
              sx: { fontSize: "0.9rem", alignItems: "flex-start" },
              endAdornment: (
                <Box sx={{ pt: 0.25 }}>
                  <VoiceToTextButton
                    disabled={disabled}
                    onActiveChange={setVoiceActive}
                    onTranscript={(text) => {
                      const cur = descRef.current || "";
                      onChangeDescription(cur ? `${cur}\n\n${text}` : text);
                    }}
                  />
                </Box>
              ),
            }}
          />
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="flex-start"
            sx={{ mt: 0.5, ml: 0.25 }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: theme.palette.text.secondary,
                mt: 0.15,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.35 }}
            >
              {matchedPatientName
                ? t(
                    "patients_v2.description_autosend_hint_existing",
                    "This will be added as a new chat message to {name} and processed automatically.",
                  ).replace("{name}", matchedPatientName)
                : t(
                    "patients_v2.description_autosend_hint",
                    "This will be the patient's first chat message and is processed automatically.",
                  )}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Collapse>
  );
};

export default AddMoreDetailsPanel;
